import type { Placement } from "@floating-ui/react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import type { Dispatch, HTMLProps, ReactElement, ReactNode, SetStateAction } from "react";
import { cloneElement, createContext, isValidElement, useContext, useMemo, useState } from "react";

import { mergeRefs } from "@/helpers/mergeRefs";

interface FloatingPopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  distance?: number;
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function useFloatingPopover({
  initialOpen = false,
  placement = "bottom",
  distance = 5,
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: FloatingPopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(distance),
      flip({ crossAxis: placement.includes("-"), fallbackAxisSideDirection: "end", padding: 5 }),
      shift({ padding: 5 }),
    ],
  });
  const click = useClick(data.context, { enabled: controlledOpen == null });
  const interactions = useInteractions([click, useDismiss(data.context), useRole(data.context)]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [open, setOpen, interactions, data, modal, labelId, descriptionId],
  );
}

type ContextType = ReturnType<typeof useFloatingPopover> & {
  setLabelId: Dispatch<SetStateAction<string | undefined>>;
  setDescriptionId: Dispatch<SetStateAction<string | undefined>>;
};

const FloatingPopoverContext = createContext<ContextType | null>(null);

function useFloatingPopoverContext(): ContextType {
  const context = useContext(FloatingPopoverContext);
  if (context == null) {
    throw new Error("FloatingPopover components must be wrapped in <FloatingPopover />");
  }

  return context;
}

export function FloatingPopover({
  children,
  modal = false,
  ...options
}: { children: ReactNode } & FloatingPopoverOptions): ReactNode {
  const popover = useFloatingPopover({ modal, ...options });

  return <FloatingPopoverContext value={popover}>{children}</FloatingPopoverContext>;
}

interface FloatingPopoverTriggerProps extends Omit<HTMLProps<HTMLElement>, "children"> {
  children: ReactNode;
  asChild?: boolean;
  ref?: React.Ref<HTMLElement>;
}

export function FloatingPopoverTrigger({
  children,
  asChild = false,
  ref: propRef,
  ...props
}: FloatingPopoverTriggerProps): ReactNode {
  const context = useFloatingPopoverContext();
  const childrenRef = (children as { ref?: React.Ref<HTMLElement> }).ref;
  const ref = mergeRefs(context.refs.setReference, propRef, childrenRef);

  if (asChild && isValidElement(children)) {
    const referenceProps = {
      ref,
      ...props,
      ...(children.props as object),
      "data-state": context.open ? "open" : "closed",
    };

    // Floating UI merges the child and trigger event handlers while Ark-style
    // `asChild` composition keeps the caller's interactive element intact.
    // oxlint-disable-next-line react-hooks/refs
    return cloneElement(children, context.getReferenceProps(referenceProps));
  }

  return (
    <button ref={ref} type="button" data-state={context.open ? "open" : "closed"} {...context.getReferenceProps(props)}>
      {children}
    </button>
  );
}

export function FloatingPopoverContent({ children }: { children: ReactElement }): ReactNode {
  const { context: floatingContext, ...context } = useFloatingPopoverContext();
  const childRef = (children as { ref?: React.Ref<HTMLElement> }).ref;
  const ref = mergeRefs(context.refs.setFloating, childRef);

  if (!floatingContext.open) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager context={floatingContext} modal={context.modal}>
        {cloneElement(
          children,
          context.getFloatingProps({
            ref,
            style: context.floatingStyles,
            "aria-labelledby": context.labelId,
            "aria-describedby": context.descriptionId,
            ...(children.props as object),
          }),
        )}
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
